// src/data/skills.ts
import type { IconType } from 'react-icons';
import {
    TbAxe, TbBarbell, TbBrain, TbCoinEuro, TbFlame,
    TbFlask2, TbHeartbeat, TbPick, TbRun,
    TbTarget, TbTools, TbToolsKitchen2,
} from 'react-icons/tb';
import type { PlayerAttributes } from '../types';

/**
 * Every levelled skill a character can hold, and the groups they are shown in (55.47).
 *
 * THE ONE PLACE A SKILL IS NAMED. Before this the five physical attributes were spelled out in
 * six files — the dashboard, the profile, the course cards, the training calendar, the abilities
 * panel, the admin tools — each with its own list, and they had already drifted: the dashboard
 * still showed the workshop's six crafts a month after the workshop went, and none of them showed
 * Rahandus. A page asks this module which groups are on and renders what it is given.
 *
 * ADDING A SKILL is an entry here plus whatever on the server earns it: the profile, the dashboard
 * and the course requirements pick it up without a change of their own.
 *
 * A GROUP HAS ONE OF TWO STATES. `live` is shown everywhere and trained somewhere. `soon` is
 * announced: the profile shows it, marked as in development, and nothing else does — the dashboard,
 * the admin panel and the service record's totals stay on what can actually be earned. Opening a
 * feature is moving its group from `soon` to `live`. (A third, `off`, held the removed workshop's
 * and vehicles' skills until the cleanup of October 2026 removed them.)
 *
 * THE SIX CRAFT SKILLS OF 55.53 START FROM ZERO, by the owner's decision of 29 September; the old
 * workshop's levels were not carried over. The keys are ASCII Estonian, like `rahandus` and the
 * room ids (`kook`, `tookoda`).
 *
 * Names and hues match what the pages already used; the five physical hues are the training
 * centre's (styles/pages/Training.css).
 */

export type SkillId = keyof PlayerAttributes;

export type SkillGroupId = 'physical' | 'finance' | 'production' | 'crafts';

/** `live`: shown and trained. `soon`: announced on the profile only. */
export type SkillGroupStatus = 'live' | 'soon';

export interface SkillGroup {
    id: SkillGroupId;
    name: string;
    /** One line on what the group is for, in the player's language. */
    description: string;
    /** Where the group is trained, when a page trains it. */
    trainedAt: { label: string; path: string } | null;
    status: SkillGroupStatus;
}

export interface SkillDef {
    id: SkillId;
    group: SkillGroupId;
    name: string;
    /** What the skill does, where there is something to say beyond its group. */
    note?: string;
    hue: string;
    icon: IconType;
}

export const SKILL_GROUPS: SkillGroup[] = [
    {
        id: 'physical',
        name: 'Füüsilised omadused',
        description: 'Koolituste nõuded, patrullisündmuste kontrollid ja ametikoha palk loevad neid.',
        trainedAt: { label: 'Treeningkeskus', path: '/training' },
        status: 'live',
    },
    {
        id: 'finance',
        name: 'Rahandus',
        description: 'Kasvab kasumiga suletud investeeringutest ja tasulistest rahanduse koolitustest.',
        trainedAt: { label: 'Pank', path: '/bank' },
        status: 'live',
    },
    {
        id: 'production',
        name: 'Tootmine',
        description: 'Toormaterjal kodu ehitamiseks: puit, kivi ja metall. Kasvab iga töötunniga ja kiirendab tööd.',
        trainedAt: { label: 'Tootmine', path: '/production' },
        status: 'live',
    },
    {
        id: 'crafts',
        name: 'Töökoda, labor ja köök',
        description: 'Kodu ruumides tehtav töö.',
        trainedAt: null,
        status: 'soon',
    },
];

export const SKILLS: SkillDef[] = [
    { id: 'strength', group: 'physical', name: 'Jõud', note: 'Tõstab maksimaalset tervist.', hue: '#ff7a59', icon: TbBarbell },
    { id: 'agility', group: 'physical', name: 'Kiirus', hue: '#4fd1c5', icon: TbRun },
    { id: 'dexterity', group: 'physical', name: 'Osavus', hue: '#ffb020', icon: TbTarget },
    { id: 'intelligence', group: 'physical', name: 'Intelligentsus', hue: '#8ab4ff', icon: TbBrain },
    { id: 'endurance', group: 'physical', name: 'Vastupidavus', note: 'Tõstab maksimaalset tervist.', hue: '#5ad48f', icon: TbHeartbeat },
    { id: 'rahandus', group: 'finance', name: 'Rahandus', note: 'Parandab hoiuste intressi.', hue: '#f2d27a', icon: TbCoinEuro },
    { id: 'metsandus', group: 'production', name: 'Metsandus', note: 'Palgid, lauad ja prussid.', hue: '#8fc46a', icon: TbAxe },
    { id: 'kaevandus', group: 'production', name: 'Kaevandus', note: 'Kivi, tsement ja ehitusplokid.', hue: '#c4b49a', icon: TbPick },
    { id: 'metallitoo', group: 'production', name: 'Metallitöö', note: 'Maak, metall ja näiteks katuseplekk.', hue: '#ff8f5a', icon: TbFlame },
    { id: 'kasitoo', group: 'crafts', name: 'Käsitöö', note: 'Kodu töökojas.', hue: '#c79a6b', icon: TbTools },
    { id: 'keemia', group: 'crafts', name: 'Keemia', note: 'Kodu laboris.', hue: '#9be37a', icon: TbFlask2 },
    { id: 'kokkamine', group: 'crafts', name: 'Kokkamine', note: 'Kodu köögis.', hue: '#ff9f6b', icon: TbToolsKitchen2 },
];

const BY_ID = new Map<string, SkillDef>(SKILLS.map((s) => [s.id, s]));

export const getSkill = (id: string): SkillDef | undefined => BY_ID.get(id);

/** The skill's name, or the id itself for something this registry does not know. */
export const skillName = (id: string): string => BY_ID.get(id)?.name ?? id;

const groupsIn = (...states: SkillGroupStatus[]): Array<SkillGroup & { skills: SkillDef[] }> =>
    SKILL_GROUPS.filter((g) => states.includes(g.status)).map((g) => ({ ...g, skills: SKILLS.filter((s) => s.group === g.id) }));

/** The open groups — what the dashboard, the admin panel and the totals work with. */
export const activeSkillGroups = () => groupsIn('live');

/** Every skill in an open group. */
export const activeSkills = (): SkillDef[] => activeSkillGroups().flatMap((g) => g.skills);

/** What the profile shows: the open groups, then the ones announced as in development. */
export const profileSkillGroups = () => groupsIn('live', 'soon');
